import * as itemStyles from "./ComboboxItem.css";

import {
  type JSX,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { Icon } from "@frontend/components/atoms/Icon";
import { useIslandHost } from "@frontend/contexts";

import { Combobox } from "./Combobox";
import {
  type ComboboxDataProps,
  type ComboboxOption,
  comboboxDataSchema,
} from "./Combobox.schema";
import { ComboboxContent } from "./ComboboxContent";
import { ComboboxGroup } from "./ComboboxGroup";
import { ComboboxGroupLabel } from "./ComboboxGroupLabel";
import { ComboboxIcon } from "./ComboboxIcon";
import { ComboboxInput } from "./ComboboxInput";
import { ComboboxItem } from "./ComboboxItem";
import { ComboboxLabel } from "./ComboboxLabel";
import { ComboboxTrigger } from "./ComboboxTrigger";
import { ComboboxValue } from "./ComboboxValue";
import { ComboboxVirtualizedList } from "./ComboboxVirtualizedList";
import {
  COMBOBOX_GROUP_TAG_NAME,
  COMBOBOX_ITEM_TAG_NAME,
  COMBOBOX_TAG_NAME,
} from "./constants";
import { useComboboxVirtualizedScroll } from "./useComboboxVirtualizedScroll";

export interface ComboboxHandle {
  select: (id: string | null) => void;
  update: (items: ComboboxOption[], selectedId?: string | null) => void;
  empty: () => void;
  disable: () => void;
  enable: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [COMBOBOX_TAG_NAME]: { props: ComboboxDataProps; handle: ComboboxHandle };
  }
  interface InertTagNames {
    [COMBOBOX_ITEM_TAG_NAME]: true;
    [COMBOBOX_GROUP_TAG_NAME]: true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-combobox": HTMLElement & ComboboxHandle;
  }
}

const RWCComboboxItem = rwc(COMBOBOX_ITEM_TAG_NAME);

const RWCComboboxGroup = rwc(COMBOBOX_GROUP_TAG_NAME);

function readComboboxItems(
  scope: ParentNode,
  group?: string,
): ComboboxOption[] {
  return Array.from(
    scope.querySelectorAll<HTMLElement>(`:scope > ${RWCComboboxItem.tagName}`),
  )
    .filter((item) => item.getAttribute("value") !== "")
    .map((item) => ({
      id: item.getAttribute("value") ?? "",
      text: item.textContent?.trim() ?? "",
      ...(group !== undefined ? { group } : {}),
      ...item.dataset,
    }));
}

export function readComboboxSlotted(
  el: HTMLElement,
): Partial<ComboboxDataProps> {
  const groupEls = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCComboboxGroup.tagName}`),
  );

  const items: ComboboxOption[] = [
    ...readComboboxItems(el),
    ...groupEls.flatMap((groupEl) =>
      readComboboxItems(groupEl, groupEl.getAttribute("label") ?? ""),
    ),
  ];

  const itemEls = el.querySelectorAll<HTMLElement>(
    [
      `:scope > ${RWCComboboxItem.tagName}`,
      `:scope > ${RWCComboboxGroup.tagName} > ${RWCComboboxItem.tagName}`,
    ].join(", "),
  );
  const selectedItem = Array.from(itemEls).find((item) =>
    item.hasAttribute("selected"),
  );

  const defaultValue =
    el.getAttribute("value") ??
    el.dataset.initialId ??
    selectedItem?.getAttribute("value") ??
    null;

  if (defaultValue) {
    el.setAttribute("value", defaultValue);
  }

  return {
    items,
    defaultValue,
    onValueChange: (id, item) => {
      if (id) {
        el.setAttribute("value", id);
      } else {
        el.removeAttribute("value");
      }

      el.dispatchEvent(
        new CustomEvent("combobox:change", {
          detail: { id, item },
          bubbles: true,
        }),
      );
    },
    onSelect: (item) => {
      el.dispatchEvent(
        new CustomEvent("combobox:select", {
          detail: { data: item },
          bubbles: true,
        }),
      );
    },
  };
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function matchesQuery(item: ComboboxOption, query: string): boolean {
  const normalized = normalize(query);

  return (
    normalize(item.text).includes(normalized) ||
    (item.description !== undefined &&
      normalize(item.description).includes(normalized))
  );
}

interface CreatableOption extends ComboboxOption {
  __create: true;
}

function isCreatable(item: ComboboxOption): item is CreatableOption {
  return item.__create === true;
}

interface ComboboxOptionGroup {
  value: string;
  items: ComboboxOption[];
}

function groupComboboxItems(items: ComboboxOption[]): ComboboxOptionGroup[] {
  const groups = new Map<string, ComboboxOption[]>();

  for (const item of items) {
    const key = item.group ?? "";
    const bucket = groups.get(key);

    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return Array.from(groups, ([value, groupItems]) => ({
    value,
    items: groupItems,
  }));
}

function renderComboboxItem(item: ComboboxOption) {
  return isCreatable(item) ? (
    <BaseCombobox.Item key={item.id} value={item} className={itemStyles.item}>
      <span className={itemStyles.itemText}>
        Create &quot;{item.text}&quot;
      </span>
      <span className={itemStyles.itemIndicator}>
        <Icon name="Plus" size="sm" />
      </span>
    </BaseCombobox.Item>
  ) : (
    <ComboboxItem
      key={item.id}
      value={item}
      description={item.description}
      status={item.status}
    >
      {item.text}
    </ComboboxItem>
  );
}

// Web-component bridge for `<rwc-combobox>`
function RWCComboboxComponent({
  ref,
  items: initialItems = [],
  defaultValue,
  disabled: initialDisabled,
  label,
  size,
  placeholder = "…",
  searchPlaceholder = "Search…",
  noResultsText = "No results",
  popupWidth,
  clearable = true,
  inputInsidePopup = true,
  freeText = false,
  required,
  name,
  virtualized = false,
  onValueChange,
  onSelect,
}: Partial<ComboboxDataProps> & { ref?: Ref<ComboboxHandle> }): JSX.Element {
  const host = useIslandHost();
  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<ComboboxOption>();

  const [items, setItems] = useState(initialItems);
  const [value, setValue] = useState(defaultValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [dynamicItems, setDynamicItems] = useState<ComboboxOption[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  useEffect(() => {
    if (virtualized && popupWidth === "content") {
      console.warn(
        '[Combobox] popup-width="content" has no effect on a virtualized list, whose options are rendered out of flow. Use a CSS length instead.',
      );
    }
  }, [virtualized, popupWidth]);

  useImperativeHandle(
    ref,
    () => ({
      select: (id) => setValue(id),
      update: (nextItems, selectedId = null) => {
        setItems(nextItems);
        setValue(selectedId);
      },
      empty: () => {
        setItems([]);
        setValue(null);
      },
      disable: () => setDisabled(true),
      enable: () => setDisabled(false),
    }),
    [],
  );

  const allItems = useMemo(() => {
    if (dynamicItems.length === 0) {
      return items;
    }

    const knownIds = new Set(items.map((item) => item.id));

    return [...items, ...dynamicItems.filter((item) => !knownIds.has(item.id))];
  }, [items, dynamicItems]);

  const trimmedQuery = query.trim();

  const itemsForView = useMemo(() => {
    if (!freeText || trimmedQuery === "") {
      return allItems;
    }

    const alreadyExists = allItems.some(
      (item) => normalize(item.text) === normalize(trimmedQuery),
    );

    if (alreadyExists) {
      return allItems;
    }

    const creatable: CreatableOption = {
      id: `__create__${trimmedQuery}`,
      text: trimmedQuery,
      __create: true,
    };

    return [...allItems, creatable];
  }, [allItems, freeText, trimmedQuery]);

  const hasDescriptions = useMemo(
    () => itemsForView.some((item) => item.description !== undefined),
    [itemsForView],
  );

  const groupedItemsForView = useMemo(() => {
    const isGrouped = itemsForView.some((item) => item.group !== undefined);

    return isGrouped ? groupComboboxItems(itemsForView) : null;
  }, [itemsForView]);

  const resolvedValue = useMemo<ComboboxOption | null>(() => {
    const found = allItems.find((item) => item.id === value) ?? null;

    if (found) {
      return found;
    }

    if (freeText && value) {
      return { id: value, text: value };
    }

    return null;
  }, [allItems, value, freeText]);

  function commit(
    nextId: string | null,
    item: ComboboxOption | null,
    interactive: boolean,
  ) {
    setValue(nextId);
    onValueChange?.(nextId, item);

    getElementInternals(host)?.setFormValue(disabled ? null : nextId);

    if (interactive && item) {
      onSelect?.(item);
    }
  }

  function handleRootValueChange(nextItem: ComboboxOption | null) {
    if (nextItem && isCreatable(nextItem)) {
      const text = nextItem.text.trim();

      if (!text) {
        return;
      }

      const existing = allItems.find(
        (item) => normalize(item.text) === normalize(text),
      );
      const created = existing ?? { id: text, text };

      if (!existing) {
        setDynamicItems((prev) => [...prev, created]);
      }

      commit(created.id, created, true);
      setQuery("");

      return;
    }

    commit(nextItem?.id ?? null, nextItem, true);
    setQuery("");
  }

  return (
    <Combobox<ComboboxOption>
      items={groupedItemsForView ?? itemsForView}
      value={resolvedValue}
      onValueChange={handleRootValueChange}
      inputValue={query}
      onInputValueChange={setQuery}
      itemToStringLabel={(item) => item.text}
      itemToStringValue={(item) => item.id}
      isItemEqualToValue={(a, b) => a.id === b.id}
      filter={(item, filterQuery) =>
        isCreatable(item) || matchesQuery(item, filterQuery)
      }
      autoHighlight
      disabled={disabled}
      required={required}
      name={name}
      size={size}
      virtualized={virtualized}
      onItemHighlighted={virtualized ? onItemHighlighted : undefined}
    >
      {label ? <ComboboxLabel>{label}</ComboboxLabel> : null}

      {inputInsidePopup ? (
        <ComboboxTrigger>
          <ComboboxValue placeholder={placeholder} />
          <ComboboxIcon />
        </ComboboxTrigger>
      ) : (
        <ComboboxInput placeholder={placeholder} clearable={clearable} />
      )}

      <ComboboxContent
        popupWidth={popupWidth}
        input={
          inputInsidePopup ? (
            <ComboboxInput
              placeholder={searchPlaceholder}
              showTrigger={false}
              clearable={clearable}
            />
          ) : undefined
        }
        empty={noResultsText}
      >
        {virtualized ? (
          <ComboboxVirtualizedList<ComboboxOption>
            estimateSize={() => (hasDescriptions ? 56 : 36)}
            itemKey={(item) => item.id}
            description={(item) => item.description}
            virtualizerRef={virtualizerRef}
          >
            {(item) =>
              isCreatable(item) ? (
                <>Create &quot;{item.text}&quot;</>
              ) : (
                item.text
              )
            }
          </ComboboxVirtualizedList>
        ) : groupedItemsForView ? (
          (group: ComboboxOptionGroup) => (
            <ComboboxGroup key={group.value} items={group.items}>
              {group.value ? (
                <ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>
              ) : null}
              <BaseCombobox.Collection>
                {renderComboboxItem}
              </BaseCombobox.Collection>
            </ComboboxGroup>
          )
        ) : (
          renderComboboxItem
        )}
      </ComboboxContent>
    </Combobox>
  );
}

componentManager.register(
  RWCComboboxItem,
  RWCComboboxGroup,
  rwc(COMBOBOX_TAG_NAME, {
    component: RWCComboboxComponent,
    dataSchema: comboboxDataSchema,
    usesSlots: true,
    readSlotted: readComboboxSlotted,
    formAssociated: true,
  }),
);
